import React, { useState } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import { Send as SendIcon, HelpOutline as QuestionIcon } from '@mui/icons-material';

/**
 * QuestionCard — Renders interactive questions from the ask_user tool.
 *
 * Supports:
 * - Multiple choice (radio buttons) with descriptions
 * - Multi-select (checkboxes)
 * - Open-ended text input
 * - "Other" custom input for choice questions
 * - Submit button sends all answers as structured message
 *
 * Props:
 *   questions: [{header, question, options: [{label, description}], multi_select}]
 *   context: string (why these questions are asked)
 *   onSubmit: (formattedAnswer: string) => void
 *   disabled: boolean
 */
const QuestionCard = ({ questions = [], context = '', onSubmit, disabled = false }) => {
  const [answers, setAnswers] = useState({});
  const [otherTexts, setOtherTexts] = useState({});
  const [submitted, setSubmitted] = useState(false);

  if (!questions || questions.length === 0) return null;

  const handleRadioChange = (header, value) => {
    setAnswers(prev => ({ ...prev, [header]: value }));
  };

  const handleCheckboxChange = (header, label, checked) => {
    setAnswers(prev => {
      const current = prev[header] || [];
      if (checked) {
        return { ...prev, [header]: [...current, label] };
      }
      return { ...prev, [header]: current.filter(v => v !== label) };
    });
  };

  const handleTextChange = (header, value) => {
    setAnswers(prev => ({ ...prev, [header]: value }));
  };

  const handleSubmit = () => {
    if (disabled || submitted) return;

    // Format answers as readable text for the conversation
    const lines = [];
    questions.forEach(q => {
      const answer = answers[q.header];
      if (answer === '__other__') {
        lines.push(`**${q.header}:** ${otherTexts[q.header] || 'Not specified'}`);
      } else if (Array.isArray(answer)) {
        const items = answer.map(a => a === '__other__' ? otherTexts[q.header] || 'Other' : a);
        lines.push(`**${q.header}:** ${items.join(', ')}`);
      } else if (answer) {
        lines.push(`**${q.header}:** ${answer}`);
      } else {
        lines.push(`**${q.header}:** Not answered`);
      }
    });

    setSubmitted(true);
    if (onSubmit) {
      onSubmit(lines.join('\n'));
    }
  };

  const allAnswered = questions.every(q => {
    const a = answers[q.header];
    if (q.options) {
      if (q.multi_select) return Array.isArray(a) && a.length > 0;
      return a && a !== '';
    }
    return a && a.trim() !== '';
  });

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: submitted ? 'success.light' : 'primary.light',
      borderRadius: 2,
      overflow: 'hidden',
      mb: 2,
      opacity: submitted ? 0.7 : 1,
    }}>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.5,
        bgcolor: submitted ? 'success.50' : 'primary.50',
        borderBottom: '1px solid',
        borderColor: submitted ? 'success.light' : 'primary.light',
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <QuestionIcon sx={{ fontSize: 20, color: submitted ? 'success.main' : 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: submitted ? 'success.dark' : 'primary.dark' }}>
          {submitted ? 'Answered' : 'Questions before proceeding'}
        </Typography>
      </Box>

      {/* Context */}
      {context && (
        <Box sx={{ px: 2, pt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {context}
          </Typography>
        </Box>
      )}

      {/* Questions */}
      <Box sx={{ px: 2, py: 1.5 }}>
        {questions.map((q, idx) => (
          <Box key={q.header || idx} sx={{ mb: idx < questions.length - 1 ? 2.5 : 0 }}>
            {/* Question header + text */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip label={q.header} size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {q.question}
              </Typography>
            </Box>

            {/* Options or text input */}
            {q.options ? (
              q.multi_select ? (
                // Multi-select checkboxes
                <FormGroup sx={{ pl: 1 }}>
                  {q.options.map(opt => (
                    <FormControlLabel
                      key={opt.label}
                      disabled={disabled || submitted}
                      control={
                        <Checkbox
                          size="small"
                          checked={(answers[q.header] || []).includes(opt.label)}
                          onChange={(e) => handleCheckboxChange(q.header, opt.label, e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.label}</Typography>
                          {opt.description && (
                            <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                  <FormControlLabel
                    disabled={disabled || submitted}
                    control={
                      <Checkbox
                        size="small"
                        checked={(answers[q.header] || []).includes('__other__')}
                        onChange={(e) => handleCheckboxChange(q.header, '__other__', e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2" color="text.secondary">Other...</Typography>}
                  />
                  {(answers[q.header] || []).includes('__other__') && (
                    <TextField
                      size="small" fullWidth placeholder="Enter your answer..."
                      value={otherTexts[q.header] || ''}
                      onChange={(e) => setOtherTexts(prev => ({ ...prev, [q.header]: e.target.value }))}
                      disabled={disabled || submitted}
                      sx={{ ml: 4, mt: 0.5, maxWidth: 300 }}
                    />
                  )}
                </FormGroup>
              ) : (
                // Single-select radio buttons
                <RadioGroup
                  value={answers[q.header] || ''}
                  onChange={(e) => handleRadioChange(q.header, e.target.value)}
                  sx={{ pl: 1 }}
                >
                  {q.options.map(opt => (
                    <FormControlLabel
                      key={opt.label}
                      value={opt.label}
                      disabled={disabled || submitted}
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{opt.label}</Typography>
                          {opt.description && (
                            <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                  <FormControlLabel
                    value="__other__"
                    disabled={disabled || submitted}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2" color="text.secondary">Other...</Typography>}
                  />
                  {answers[q.header] === '__other__' && (
                    <TextField
                      size="small" fullWidth placeholder="Enter your answer..."
                      value={otherTexts[q.header] || ''}
                      onChange={(e) => setOtherTexts(prev => ({ ...prev, [q.header]: e.target.value }))}
                      disabled={disabled || submitted}
                      sx={{ ml: 4, mt: 0.5, maxWidth: 300 }}
                    />
                  )}
                </RadioGroup>
              )
            ) : (
              // Open-ended text input
              <TextField
                size="small" fullWidth multiline minRows={1} maxRows={3}
                placeholder="Type your answer..."
                value={answers[q.header] || ''}
                onChange={(e) => handleTextChange(q.header, e.target.value)}
                disabled={disabled || submitted}
                sx={{ mt: 0.5 }}
              />
            )}

            {idx < questions.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Box>

      {/* Submit button */}
      {!submitted && (
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleSubmit}
            disabled={disabled || !allAnswered}
            startIcon={<SendIcon />}
            sx={{ textTransform: 'none' }}
          >
            Answer & Continue
          </Button>
        </Box>
      )}
    </Box>
  );
};

/**
 * Parse ask_user JSON from a message content string.
 * Returns null if not an ask_user message.
 */
export const parseAskUserContent = (content) => {
  if (!content) return null;

  // Try to find JSON with type: "ask_user" in the content
  // It could be the entire content or embedded in text
  try {
    // Direct JSON parse
    const parsed = JSON.parse(content);
    if (parsed.type === 'ask_user' && parsed.questions) {
      return parsed;
    }
  } catch {
    // Not pure JSON — look for embedded JSON
    const jsonMatch = content.match(/\{[\s\S]*"type"\s*:\s*"ask_user"[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.questions) return parsed;
      } catch {
        // Not valid JSON
      }
    }
  }

  return null;
};

export default QuestionCard;
